import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTodaySummary, updatePlannerStatus } from "./TodayService";
import { listHabits, checkIn } from "../habits/HabitsService";
import { apiFetch } from "../api";
import { BASE_URL } from "../../commonUrls";
import LifePulseGauge from "./LifePulseGauge";
import "./Today.css";

const VIEW_KEY = "lifeos-view-mode";
const VIEW_OPTIONS = [
  { label: "Dark",      value: "dark" },
  { label: "Light",     value: "light" },
  { label: "Developer", value: "developer" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 5)  return { text: "Still up?",        emoji: "🌙" };
  if (h < 12) return { text: "Good morning",     emoji: "☀️" };
  if (h < 17) return { text: "Good afternoon",   emoji: "🌤" };
  if (h < 21) return { text: "Good evening",     emoji: "🌇" };
  return       { text: "Good night",             emoji: "🌙" };
}

function todayLabel() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long",
  });
}

export default function Today() {
  const nav = useNavigate();
  const [summary, setSummary]       = useState({ urgent: [], reminders: [], payments: [] });
  const [habits, setHabits]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [rangeDays, setRangeDays]   = useState(7);
  const [updatingId, setUpdatingId] = useState("");
  const [toast, setToast]           = useState({ message: "", type: "" });
  const [viewMode, setViewMode]     = useState("dark");
  const [aiBriefing, setAiBriefing] = useState("");
  const [aiLoading, setAiLoading]   = useState(false);
  const [habitCheckinNote, setHabitCheckinNote] = useState({ id: null, text: "" });
  const { text: greet, emoji: greetEmoji } = greeting();

  useEffect(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    if (stored && VIEW_OPTIONS.some(o => o.value === stored)) setViewMode(stored);
  }, []);

  useEffect(() => {
    document.body.dataset.viewMode = viewMode;
    localStorage.setItem(VIEW_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    Promise.all([
      getTodaySummary(rangeDays),
      listHabits(),
    ]).then(([todayData, habitData]) => {
      if (!alive) return;
      setSummary({
        urgent:    todayData?.urgent    || [],
        reminders: todayData?.reminders || [],
        payments:  todayData?.payments  || [],
      });
      setHabits(Array.isArray(habitData) ? habitData.filter(h => h.active) : []);
    }).catch(() => {
      if (alive) setError("Unable to load today summary.");
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, [rangeDays]);

  const formatDate = (d) => {
    if (!d) return "No date";
    return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  const splitByDate = (items, dateKey) => {
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const passed = [], upcoming = [];
    items.forEach(item => {
      const raw = item[dateKey];
      if (!raw) { upcoming.push(item); return; }
      new Date(raw + "T00:00:00") < startOfToday ? passed.push(item) : upcoming.push(item);
    });
    return { passed, upcoming };
  };

  const counts = useMemo(() => ({
    urgent:    summary.urgent.length,
    reminders: summary.reminders.length,
    payments:  summary.payments.length,
  }), [summary]);

  const habitsDueToday    = habits.filter(h => h.frequency === "DAILY" && !h.completedToday);
  const habitsCompletedToday = habits.filter(h => h.completedToday);
  const overdueCount      = summary.urgent.filter(i => {
    const d = i.dueDate ? new Date(i.dueDate + "T00:00:00") : null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return d && d < today;
  }).length;
  const todayPayments = (splitByDate(summary.payments, "paymentDate").upcoming || [])
    .filter(p => {
      const d = p.paymentDate ? new Date(p.paymentDate + "T00:00:00") : null;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return d && d.getTime() === today.getTime();
    });

  const handleStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await updatePlannerStatus(id, status);
      setSummary(prev => ({
        urgent:    prev.urgent.filter(i => i.id !== id),
        reminders: prev.reminders.filter(i => i.id !== id),
        payments:  prev.payments.filter(i => i.id !== id),
      }));
      showToast(`Marked as ${status.toLowerCase()}.`, "success");
    } catch {
      showToast("Update failed. Try again.", "error");
    } finally {
      setUpdatingId("");
    }
  };

  const handleHabitCheckIn = async (id, note = "") => {
    const updated = await checkIn(id, note).catch(() => null);
    if (updated) {
      setHabits(prev => prev.map(h => h.id === id ? updated : h));
      setHabitCheckinNote({ id: null, text: "" });
      showToast("Habit checked in!", "success");
    }
  };

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 2500);
  };

  const handleAiBriefing = async () => {
    setAiLoading(true);
    setAiBriefing("");
    const overdue = summary.urgent.filter(i => {
      const d = i.dueDate ? new Date(i.dueDate + "T00:00:00") : null;
      const today = new Date(); today.setHours(0,0,0,0);
      return d && d < today;
    });
    try {
      const res = await apiFetch(`${BASE_URL}/assistant`, {
        method: "POST",
        body: JSON.stringify({
          action: "CHAT",
          input: `Give me a concise morning briefing for today in 3-4 sentences. Here's my status:
- Overdue tasks: ${overdue.length} (${overdue.slice(0, 3).map(i => i.title).join(", ") || "none"})
- Tasks/reminders in next ${rangeDays} days: ${counts.urgent + counts.reminders}
- Payments due: ${counts.payments} (${summary.payments.slice(0, 2).map(p => p.title + (p.amount ? " ₹" + p.amount : "")).join(", ") || "none"})
- Habits pending today: ${habitsDueToday.map(h => h.name).join(", ") || "none"}
- Habits already done: ${habitsCompletedToday.map(h => h.name).join(", ") || "none"}
Be warm, specific, and end with one concrete suggestion for the day.`,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setAiBriefing(data.message || "");
      }
    } catch {}
    setAiLoading(false);
  };

  const rangeOptions = [
    { label: "Today",   value: 0 },
    { label: "7 days",  value: 7 },
    { label: "15 days", value: 15 },
  ];

  return (
    <div className="today-page">
      {toast.message && (
        <div className={`today-toast today-toast-${toast.type}`}>{toast.message}</div>
      )}

      {/* MORNING HEADER */}
      <div className="today-morning-header">
        <div className="today-greeting">
          <span className="today-greeting-emoji">{greetEmoji}</span>
          <div>
            <div className="today-greeting-text">{greet}</div>
            <div className="today-date-label">{todayLabel()}</div>
          </div>
        </div>
        <div className="today-topbar-right">
          <div className="today-view-switch" role="radiogroup">
            {VIEW_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`today-view-btn${viewMode === opt.value ? " active" : ""}`}
                onClick={() => setViewMode(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* LIFE PULSE */}
      {!loading && <LifePulseGauge habits={habits} />}

      {/* VITALS STRIP */}
      {!loading && (
        <div className="today-vitals">
          <VitalChip
            icon="⚠️" label="Overdue"
            value={overdueCount}
            accent={overdueCount > 0 ? "red" : "green"}
            onClick={() => nav("/planner?dateFilter=OVERDUE")}
          />
          <VitalChip
            icon="📋" label="In range"
            value={counts.urgent + counts.reminders}
            accent="amber"
            onClick={() => nav("/planner")}
          />
          <VitalChip
            icon="💳" label="Payments"
            value={counts.payments}
            accent={counts.payments > 0 ? "amber" : "green"}
            onClick={() => nav("/planner")}
          />
          <VitalChip
            icon="🔥" label="Habits done"
            value={`${habitsCompletedToday.length}/${habits.length}`}
            accent={habitsCompletedToday.length === habits.length && habits.length > 0 ? "green" : "amber"}
            onClick={() => nav("/habits")}
          />
          {todayPayments.length > 0 && (
            <VitalChip
              icon="💰" label="Due today"
              value={`₹${todayPayments.reduce((s, p) => s + Number(p.amount || 0), 0).toLocaleString("en-IN")}`}
              accent="red"
            />
          )}
        </div>
      )}

      {/* AI BRIEFING BAR */}
      <div className="today-ai-bar">
        <button className="today-ai-btn" onClick={handleAiBriefing} disabled={aiLoading || loading}>
          {aiLoading ? "Thinking…" : "✦ Morning Briefing"}
        </button>
        <div className="today-range">
          {rangeOptions.map(opt => (
            <button
              key={opt.value}
              className={`today-range-btn${rangeDays === opt.value ? " active" : ""}`}
              onClick={() => setRangeDays(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button className="today-ai-btn today-ai-btn-ghost" onClick={() => nav("/planner/new")}>
          + Add Task
        </button>
      </div>

      {aiBriefing && (
        <div className="today-ai-result">
          <button className="today-ai-close" onClick={() => setAiBriefing("")}>×</button>
          <div className="today-ai-text">{aiBriefing}</div>
        </div>
      )}

      {/* CONTENT GRID */}
      <div className="today-grid">
        {/* Habits due today */}
        {!loading && habitsDueToday.length > 0 && (
          <div className="today-card today-card-purple">
            <div className="today-card-head">
              <span className="today-card-title">Habits Today</span>
              <span className="today-card-count today-count-purple">{habitsDueToday.length}</span>
            </div>
            <div className="today-habits-list">
              {habitsDueToday.map(h => (
                <div key={h.id} className="today-habit-row">
                  <div className="today-habit-info">
                    <span className="today-habit-name">{h.name}</span>
                    {h.category && <span className="today-habit-cat">{h.category}</span>}
                  </div>
                  {habitCheckinNote.id === h.id ? (
                    <div className="today-habit-note-row">
                      <input
                        autoFocus
                        className="today-habit-note-input"
                        value={habitCheckinNote.text}
                        onChange={e => setHabitCheckinNote(prev => ({ ...prev, text: e.target.value }))}
                        placeholder="Add a note…"
                        onKeyDown={e => e.key === "Enter" && handleHabitCheckIn(h.id, habitCheckinNote.text)}
                      />
                      <button className="today-habit-btn today-habit-done-btn"
                        onClick={() => handleHabitCheckIn(h.id, habitCheckinNote.text)}>✓</button>
                      <button className="today-habit-btn"
                        onClick={() => setHabitCheckinNote({ id: null, text: "" })}>✕</button>
                    </div>
                  ) : (
                    <button
                      className="today-habit-checkin-btn"
                      onClick={() => setHabitCheckinNote({ id: h.id, text: "" })}
                    >
                      Check in
                    </button>
                  )}
                </div>
              ))}
            </div>
            {habitsCompletedToday.length > 0 && (
              <div className="today-habits-done-bar">
                ✓ {habitsCompletedToday.map(h => h.name).join(", ")} done today
              </div>
            )}
          </div>
        )}

        <TaskCard
          title="Urgent & Overdue"
          count={counts.urgent}
          accent="red"
          loading={loading}
          error={error}
          empty={summary.urgent.length === 0}
          emptyMsg="All clear."
          onAddClick={() => nav("/planner/new")}
        >
          <ItemSection
            data={splitByDate(summary.urgent, "dueDate")}
            renderItem={item => (
              <TodayRow
                key={item.id}
                title={item.title}
                meta={[item.type, item.dueDate ? formatDate(item.dueDate) : null]}
                disabled={updatingId === item.id}
                onDone={() => handleStatus(item.id, "DONE")}
                onSkip={() => handleStatus(item.id, "SKIPPED")}
              />
            )}
          />
        </TaskCard>

        <TaskCard
          title="Reminders"
          count={counts.reminders}
          accent="amber"
          loading={loading}
          error={error}
          empty={summary.reminders.length === 0}
          emptyMsg="No reminders in this range."
          onAddClick={() => nav("/planner/new")}
        >
          <ItemSection
            data={splitByDate(summary.reminders, "dueDate")}
            renderItem={item => (
              <TodayRow
                key={item.id}
                title={item.title}
                meta={[item.type, item.dueDate ? formatDate(item.dueDate) : null]}
                disabled={updatingId === item.id}
                onDone={() => handleStatus(item.id, "DONE")}
                onSkip={() => handleStatus(item.id, "SKIPPED")}
              />
            )}
          />
        </TaskCard>

        <TaskCard
          title="Payments"
          count={counts.payments}
          accent="green"
          loading={loading}
          error={error}
          empty={summary.payments.length === 0}
          emptyMsg="No payments due."
          onAddClick={() => nav("/planner/new")}
        >
          <ItemSection
            data={splitByDate(summary.payments, "paymentDate")}
            renderItem={item => (
              <TodayRow
                key={item.id}
                title={item.title}
                meta={[
                  item.paymentType,
                  item.paymentDate ? formatDate(item.paymentDate) : null,
                  item.amount != null ? `₹${Number(item.amount).toLocaleString("en-IN")}` : null,
                ]}
                disabled={updatingId === item.id}
                onDone={() => handleStatus(item.id, "DONE")}
                onSkip={() => handleStatus(item.id, "SKIPPED")}
              />
            )}
          />
        </TaskCard>

        {/* QUICK LINKS */}
        <div className="today-card today-card-actions">
          <div className="today-card-head">
            <span className="today-card-title">Quick Links</span>
          </div>
          <div className="today-actions-grid">
            {[
              { label: "Planner",   emoji: "🗓",  path: "/planner" },
              { label: "Finance",   emoji: "💰",  path: "/finance" },
              { label: "Net Worth", emoji: "📊",  path: "/finance/net-worth" },
              { label: "Calendar",  emoji: "📅",  path: "/calendar" },
              { label: "Journal",   emoji: "📓",  path: "/journal" },
              { label: "Notes",     emoji: "📝",  path: "/notes" },
              { label: "Habits",    emoji: "🔥",  path: "/habits" },
              { label: "Review",    emoji: "📈",  path: "/review" },
            ].map(({ label, emoji, path }) => (
              <button key={path} className="today-action-btn" onClick={() => nav(path)}>
                <span className="today-action-emoji">{emoji}</span>
                <span className="today-action-label">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VitalChip({ icon, label, value, accent, onClick }) {
  return (
    <button className={`today-vital-chip today-vital-${accent}`} onClick={onClick} disabled={!onClick}>
      <span className="today-vital-icon">{icon}</span>
      <span className="today-vital-value">{value}</span>
      <span className="today-vital-label">{label}</span>
    </button>
  );
}

function TaskCard({ title, count, accent, loading, error, empty, emptyMsg, onAddClick, children }) {
  return (
    <div className={`today-card today-card-${accent}`}>
      <div className="today-card-head">
        <span className="today-card-title">{title}</span>
        <span className={`today-card-count today-count-${accent}`}>{count}</span>
      </div>
      {loading ? (
        <p className="today-muted">Loading…</p>
      ) : error ? (
        <p className="today-muted today-error">{error}</p>
      ) : empty ? (
        <div className="today-empty">
          <span className="today-empty-msg">{emptyMsg}</span>
          <button className="today-empty-btn" onClick={onAddClick}>+ Add</button>
        </div>
      ) : children}
    </div>
  );
}

function ItemSection({ data, renderItem }) {
  return (
    <div className="today-items">
      {data.passed.length > 0 && (
        <>
          <div className="today-items-label today-label-overdue">Overdue</div>
          {data.passed.map(item => renderItem(item))}
        </>
      )}
      {data.upcoming.length > 0 && (
        <>
          {data.passed.length > 0 && <div className="today-items-label">Upcoming</div>}
          {data.upcoming.map(item => renderItem(item))}
        </>
      )}
    </div>
  );
}

function TodayRow({ title, meta, disabled, onDone, onSkip }) {
  return (
    <div className="today-row">
      <div className="today-row-body">
        <div className="today-row-title">{title}</div>
        <div className="today-row-meta">
          {meta.filter(Boolean).map((m, idx) => (
            <span key={idx} className="today-row-tag">{m}</span>
          ))}
        </div>
      </div>
      <div className="today-row-actions">
        <button className="today-row-btn today-btn-done" onClick={onDone} disabled={disabled}>Done</button>
        <button className="today-row-btn today-btn-skip" onClick={onSkip} disabled={disabled}>Skip</button>
      </div>
    </div>
  );
}
